import { useEffect, useState } from 'react';
import { platformApi, type PlatformPlan, type PlatformAddon } from '../../api';

// Platform-admin catalog editor: plans (monthly / annual price, trial days,
// public visibility) and add-ons (flat per-month line items that either
// grant a feature flag or lift a usage limit).

const ZAR = (n: number | null | undefined) =>
  n == null ? '—' : new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(n);

const asBool = (v: unknown) => v === true || v === 'true';

export default function PlatformPlans() {
  const [tab, setTab] = useState<'plans' | 'addons'>('plans');
  return (
    <div className="stack">
      <header>
        <h1>Plan catalog</h1>
        <p className="muted">
          Paid tiers tenants subscribe to, plus per-tenant add-ons that either flip a feature flag or lift a usage limit.
          The seed ships one plan (<code>default</code> — free 30-day trial). Add paid tiers below.
        </p>
      </header>

      <div className="row" style={{ gap: 6 }}>
        <button
          onClick={() => setTab('plans')}
          className={tab === 'plans' ? 'primary' : undefined}
        >Plans</button>
        <button
          onClick={() => setTab('addons')}
          className={tab === 'addons' ? 'primary' : undefined}
        >Add-ons</button>
      </div>

      {tab === 'plans'  ? <PlansTab  /> : <AddonsTab />}
    </div>
  );
}

// ---------- Plans tab ----------

function PlansTab() {
  const [plans, setPlans] = useState<PlatformPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<PlatformPlan | 'new' | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try { setPlans((await platformApi.listPlans()).plans); }
    catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (p: PlatformPlan) => {
    if (!confirm(`Delete plan "${p.name}" (${p.code})? Fails if any tenants are still subscribed.`)) return;
    try { await platformApi.deletePlan(p.ROWID); await load(); }
    catch (e) { setError((e as Error).message); }
  };

  return (
    <div className="stack">
      <div className="row">
        <div className="grow" />
        <button className="primary" onClick={() => setEditing('new')}>+ New plan</button>
      </div>

      {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div className="muted" style={{ padding: 18 }}>Loading…</div>
        ) : plans.length === 0 ? (
          <div className="muted" style={{ padding: 18 }}>No plans yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 48 }}>Rank</th>
                <th>Code</th>
                <th>Name</th>
                <th style={{ textAlign: 'right' }}>Monthly</th>
                <th style={{ textAlign: 'right' }}>Annual</th>
                <th style={{ textAlign: 'right' }}>Trial</th>
                <th>Flags</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={String(p.ROWID)}>
                  <td className="muted">{p.tier_rank}</td>
                  <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{p.code}</td>
                  <td style={{ fontWeight: 500 }}>
                    {p.name}
                    {p.description && <div className="muted" style={{ fontSize: 12 }}>{p.description}</div>}
                  </td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{ZAR(p.price_monthly_zar)}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{ZAR(p.price_annual_zar)}</td>
                  <td style={{ textAlign: 'right' }}>{p.trial_days} d</td>
                  <td>
                    {asBool(p.is_active) ? <span className="pill success">active</span> : <span className="pill">inactive</span>}
                    {' '}
                    {asBool(p.is_public) ? <span className="pill accent">public</span> : <span className="pill">hidden</span>}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button onClick={() => setEditing(p)}>Edit</button>
                    <button className="ghost" style={{ color: 'var(--color-danger)', marginLeft: 6 }} onClick={() => remove(p)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <PlanEditor
          plan={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function PlanEditor({ plan, onClose, onSaved }: { plan: PlatformPlan | null; onClose: () => void; onSaved: () => void }) {
  const isNew = !plan;
  const [form, setForm] = useState({
    code: plan?.code || '',
    name: plan?.name || '',
    description: plan?.description || '',
    tier_rank: String(plan?.tier_rank ?? 0),
    billing_cycle: (plan?.billing_cycle || 'monthly') as 'monthly' | 'annual',
    price_monthly_zar: String(plan?.price_monthly_zar ?? 0),
    price_annual_zar: String(plan?.price_annual_zar ?? 0),
    trial_days: String(plan?.trial_days ?? 0),
    currency: plan?.currency || 'ZAR',
    is_active: asBool(plan?.is_active) || isNew,
    is_public: asBool(plan?.is_public) || isNew,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!form.code.trim()) { setError('Code is required.'); return; }
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError(null);
    try {
      const body: Partial<PlatformPlan> = {
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description || undefined,
        tier_rank: Number(form.tier_rank) || 0,
        billing_cycle: form.billing_cycle,
        price_monthly_zar: Number(form.price_monthly_zar) || 0,
        price_annual_zar: Number(form.price_annual_zar) || 0,
        trial_days: Number(form.trial_days) || 0,
        currency: form.currency || 'ZAR',
        is_active: form.is_active,
        is_public: form.is_public,
      };
      if (isNew) await platformApi.createPlan(body);
      else       await platformApi.updatePlan(plan!.ROWID, body);
      onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div className="card" style={modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginBottom: 14 }}>{isNew ? 'New plan' : `Edit ${plan?.name}`}</h3>
        <div className="stack">
          <div className="row">
            <div style={{ width: 180 }}>
              <label>Code</label>
              <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} disabled={!isNew} placeholder="e.g. pro" />
            </div>
            <div className="grow">
              <label>Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Professional" />
            </div>
            <div style={{ width: 100 }}>
              <label>Rank</label>
              <input type="number" value={form.tier_rank} onChange={(e) => setForm({ ...form, tier_rank: e.target.value })} />
            </div>
          </div>
          <div>
            <label>Description</label>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="row">
            <div className="grow">
              <label>Monthly price (ZAR)</label>
              <input type="number" step="0.01" value={form.price_monthly_zar} onChange={(e) => setForm({ ...form, price_monthly_zar: e.target.value })} />
            </div>
            <div className="grow">
              <label>Annual price (ZAR)</label>
              <input type="number" step="0.01" value={form.price_annual_zar} onChange={(e) => setForm({ ...form, price_annual_zar: e.target.value })} />
            </div>
            <div style={{ width: 120 }}>
              <label>Trial days</label>
              <input type="number" value={form.trial_days} onChange={(e) => setForm({ ...form, trial_days: e.target.value })} />
            </div>
          </div>
          <div className="row">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, textTransform: 'none', letterSpacing: 0 }}>
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} style={{ width: 'auto' }} />
              Active
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, textTransform: 'none', letterSpacing: 0 }}>
              <input type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} style={{ width: 'auto' }} />
              Public (tenants can self-select on the Billing page)
            </label>
          </div>
          {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}
          <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={onClose} disabled={saving}>Cancel</button>
            <button className="primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Addons tab ----------

function AddonsTab() {
  const [addons, setAddons] = useState<PlatformAddon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<PlatformAddon | 'new' | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try { setAddons((await platformApi.listAddons()).addons); }
    catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (a: PlatformAddon) => {
    if (!confirm(`Delete add-on "${a.name}" (${a.code})?`)) return;
    try { await platformApi.deleteAddon(a.ROWID); await load(); }
    catch (e) { setError((e as Error).message); }
  };

  return (
    <div className="stack">
      <div className="row">
        <div className="grow" />
        <button className="primary" onClick={() => setEditing('new')}>+ New add-on</button>
      </div>

      {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div className="muted" style={{ padding: 18 }}>Loading…</div>
        ) : addons.length === 0 ? (
          <div className="muted" style={{ padding: 18 }}>No add-ons yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th style={{ textAlign: 'right' }}>Monthly</th>
                <th style={{ textAlign: 'right' }}>Annual</th>
                <th>Grants</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {addons.map((a) => (
                <tr key={String(a.ROWID)}>
                  <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{a.code}</td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{a.name}</div>
                    {a.description && <div className="muted" style={{ fontSize: 12 }}>{a.description}</div>}
                  </td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{ZAR(a.price_monthly_zar)}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{ZAR(a.price_annual_zar)}</td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {a.grants_feature_key && <div>feature: <code>{a.grants_feature_key}</code></div>}
                    {a.grants_metric_key && <div>+{a.grants_metric_delta} <code>{a.grants_metric_key}</code></div>}
                    {!a.grants_feature_key && !a.grants_metric_key && '—'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button onClick={() => setEditing(a)}>Edit</button>
                    <button className="ghost" style={{ color: 'var(--color-danger)', marginLeft: 6 }} onClick={() => remove(a)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <AddonEditor
          addon={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function AddonEditor({ addon, onClose, onSaved }: { addon: PlatformAddon | null; onClose: () => void; onSaved: () => void }) {
  const isNew = !addon;
  const [form, setForm] = useState({
    code: addon?.code || '',
    name: addon?.name || '',
    description: addon?.description || '',
    price_monthly_zar: String(addon?.price_monthly_zar ?? 0),
    price_annual_zar: String(addon?.price_annual_zar ?? 0),
    grants_feature_key: addon?.grants_feature_key || '',
    grants_metric_key: addon?.grants_metric_key || '',
    grants_metric_delta: String(addon?.grants_metric_delta ?? ''),
    is_active: asBool(addon?.is_active) || isNew,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) { setError('Code and name are required.'); return; }
    setSaving(true); setError(null);
    try {
      const body: Partial<PlatformAddon> = {
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description || undefined,
        price_monthly_zar: Number(form.price_monthly_zar) || 0,
        price_annual_zar: Number(form.price_annual_zar) || 0,
        grants_feature_key: form.grants_feature_key || undefined,
        grants_metric_key: form.grants_metric_key || undefined,
        grants_metric_delta: form.grants_metric_delta ? Number(form.grants_metric_delta) : undefined,
        is_active: form.is_active,
      };
      if (isNew) await platformApi.createAddon(body);
      else       await platformApi.updateAddon(addon!.ROWID, body);
      onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div className="card" style={modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginBottom: 14 }}>{isNew ? 'New add-on' : `Edit ${addon?.name}`}</h3>
        <div className="stack">
          <div className="row">
            <div style={{ width: 200 }}>
              <label>Code</label>
              <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} disabled={!isNew} />
            </div>
            <div className="grow">
              <label>Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
          </div>
          <div>
            <label>Description</label>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="row">
            <div className="grow">
              <label>Monthly price (ZAR)</label>
              <input type="number" step="0.01" value={form.price_monthly_zar} onChange={(e) => setForm({ ...form, price_monthly_zar: e.target.value })} />
            </div>
            <div className="grow">
              <label>Annual price (ZAR)</label>
              <input type="number" step="0.01" value={form.price_annual_zar} onChange={(e) => setForm({ ...form, price_annual_zar: e.target.value })} />
            </div>
          </div>
          <div>
            <label>Grants (optional — either a feature flag or a metric delta)</label>
            <div className="row">
              <div className="grow">
                <input value={form.grants_feature_key} onChange={(e) => setForm({ ...form, grants_feature_key: e.target.value })} placeholder="feature_key (e.g. ai_chat_unlimited)" />
              </div>
              <div className="grow">
                <input value={form.grants_metric_key} onChange={(e) => setForm({ ...form, grants_metric_key: e.target.value })} placeholder="metric_key (e.g. matters_max)" />
              </div>
              <div style={{ width: 120 }}>
                <input type="number" value={form.grants_metric_delta} onChange={(e) => setForm({ ...form, grants_metric_delta: e.target.value })} placeholder="+delta" />
              </div>
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, textTransform: 'none', letterSpacing: 0 }}>
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} style={{ width: 'auto' }} />
            Active
          </label>
          {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}
          <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={onClose} disabled={saving}>Cancel</button>
            <button className="primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
};
const modal: React.CSSProperties = { width: 720, maxWidth: '94vw', maxHeight: '88vh', overflowY: 'auto' };
